import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Calendar, Clock, AlertTriangle, ChevronRight, Bell, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Deadline Widget - Displays upcoming compliance deadlines
 * Fetches from /api/notifications/deadlines backend endpoint
 */
const DeadlineWidget = ({ 
  maxItems = 4, 
  showHeader = true, 
  compact = false,
  daysAhead = 90 
}) => {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDeadlines();
  }, [daysAhead]);

  const fetchDeadlines = async () => {
    setLoading(true);
    const data = await apiFetch(`${API}/notifications/deadlines?${new URLSearchParams({ days_ahead: daysAhead })}`);
    if (data?._error) {
      console.error('Failed to fetch deadlines:', data.message);
      setError('Failed to load deadlines');
      // Use fallback data if API fails
      setDeadlines([
        { id: 'csrd_2026_q1', name: 'CSRD Annual Report', deadline_str: '2026-04-30', days_remaining: 120, urgency: 'medium', regulation: 'CSRD' },
        { id: 'sfdr_pai_2026', name: 'SFDR PAI Statement', deadline_str: '2026-06-30', days_remaining: 180, urgency: 'low', regulation: 'SFDR' }
      ]);
    } else {
      setDeadlines(data.deadlines || []);
      setError(null);
    }
    setLoading(false);
  };

  const getUrgencyStyle = (urgency) => {
    switch (urgency) {
      case 'critical':
        return {
          bg: 'bg-red-100',
          text: 'text-red-700',
          border: 'border-red-200',
          icon: 'text-red-500',
          badge: 'bg-red-500'
        };
      case 'high':
        return {
          bg: 'bg-amber-100',
          text: 'text-amber-700',
          border: 'border-amber-200',
          icon: 'text-amber-500',
          badge: 'bg-amber-500'
        };
      case 'medium':
        return {
          bg: 'bg-blue-100',
          text: 'text-blue-700',
          border: 'border-blue-200',
          icon: 'text-blue-500',
          badge: 'bg-blue-500'
        };
      default:
        return {
          bg: 'bg-emerald-100',
          text: 'text-emerald-700',
          border: 'border-emerald-200',
          icon: 'text-emerald-500',
          badge: 'bg-emerald-500'
        };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className={`${compact ? 'p-3' : 'p-6'} bg-white border border-slate-200 rounded-xl`}>
        <div className="flex items-center justify-center py-8">
          <RefreshCw size={20} className="animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const displayDeadlines = deadlines.slice(0, maxItems);

  return (
    <div className={`bg-white border border-slate-200 rounded-xl ${compact ? 'p-3' : 'p-6'}`} data-testid="deadline-widget">
      {showHeader && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-slate-600" />
            <h3 className="text-sm font-semibold text-slate-900">Upcoming Deadlines</h3>
          </div>
          <Link
            to="/obligations/deadlines"
            className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline flex items-center gap-1"
          >
            View All <ChevronRight size={12} />
          </Link>
        </div>
      )}

      {error && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
          {error} - Showing cached data
        </div>
      )}

      <div className="space-y-3">
        {displayDeadlines.map((deadline) => {
          const style = getUrgencyStyle(deadline.urgency);
          
          return (
            <div 
              key={deadline.id} 
              className={`p-3 rounded-lg border ${style.border} ${style.bg} transition-all hover:shadow-sm`}
              data-testid={`deadline-${deadline.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {deadline.urgency === 'critical' && (
                      <AlertTriangle size={14} className={style.icon} />
                    )}
                    <span className={`text-sm font-medium ${style.text}`}>
                      {deadline.name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-600">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} />
                      {formatDate(deadline.deadline_str)}
                    </span>
                    {deadline.regulation && (
                      <span className="px-1.5 py-0.5 bg-white/60 rounded text-slate-500">
                        {deadline.regulation}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col items-end">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold text-white ${style.badge}`}>
                    {deadline.days_remaining}d
                  </span>
                  <span className="text-xs text-slate-500 mt-1">remaining</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {deadlines.length === 0 && !error && (
        <div className="py-6 text-center text-slate-500">
          <Bell size={24} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No upcoming deadlines</p>
        </div>
      )}

      {deadlines.length > maxItems && (
        <div className="mt-3 text-center">
          <Link
            to="/obligations/deadlines"
            className="text-sm text-emerald-600 hover:underline"
          >
            +{deadlines.length - maxItems} more deadlines
          </Link>
        </div>
      )}
    </div>
  );
};

/**
 * Compact Deadline Counter - Shows summary badge
 */
export const DeadlineCounter = ({ onClick }) => {
  const [urgentCount, setUrgentCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCount = async () => {
      const data = await apiFetch(`${API}/notifications/deadlines?${new URLSearchParams({ days_ahead: 30 })}`);
      if (data?._error) {
        setUrgentCount(0);
      } else {
        const deadlines = data.deadlines || [];
        const urgent = deadlines.filter(d => d.urgency === 'critical' || d.urgency === 'high').length;
        setUrgentCount(urgent);
      }
      setLoading(false);
    };
    fetchCount();
  }, []);

  if (loading) return null;

  return (
    <button
      onClick={onClick}
      className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
      data-testid="deadline-counter"
    >
      <Calendar size={20} className="text-slate-600" />
      {urgentCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {urgentCount}
        </span>
      )}
    </button>
  );
};

export default DeadlineWidget;
